import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material';
import { UploadProvider } from './upload/UploadContext';

import theme from './theme';
import SidebarLayout from './layout/SidebarLayout';
import ImagesPage from './pages/Images';
import UploadPage from './pages/Upload';
import LoginPage from './pages/Login';
import ProductsPage from './pages/Products';
import ShopsPage from './pages/Shops';
import SectionsPage from './pages/Sections';
import ListingsPage from './pages/Listings';
import WooCommerceSitesPage from './pages/WooCommerceSites';
import WooCommerceCategoriesPage from './pages/WooCommerceCategories';
import WooCommerceListingsPage from './pages/WooCommerceListings';
import WooCommerceMockupsPage from './pages/WooCommerceMockups';
import PrintifyStoresPage from './pages/PrintifyStores';
import PrintifyOrdersPage from './pages/PrintifyOrders';
import PrintifyCatalogPage from './pages/PrintifyCatalog';
import OperationsPage from './pages/Operations';
import DynamicMockupsAccountsPage from './pages/DynamicMockupsAccounts';
import DynamicMockupsCatalogsPage from './pages/DynamicMockupsCatalogs';
import DynamicMockupsPage from './pages/DynamicMockups';
import { AuthProvider } from './auth/AuthContext';
import RequireAuth from './auth/RequireAuth';
import { RealtimeProvider } from './realtime/RealtimeContext';
import { FeatureFlagsProvider, useFeatureFlags } from './features/FeatureFlagsContext';
import {
  UnifiedActivityPage,
  UnifiedAssetsPage,
  UnifiedCatalogPage,
  UnifiedCollectionsPage,
  UnifiedConnectionsPage,
  UnifiedDesignsPage,
  UnifiedListingsPage,
  UnifiedProductsPage,
} from './pages/unified/UnifiedPages';
import CanonicalProductEditor from './pages/unified/CanonicalProductEditor';
import CanonicalListingEditor from './pages/unified/CanonicalListingEditor';
import CanonicalDesignEditor from './pages/unified/CanonicalDesignEditor';
import CanonicalLaunchWizard from './pages/unified/CanonicalLaunchWizard';
import CanonicalReconcilePage from './pages/unified/CanonicalReconcilePage';
import UnifiedHomePage from './pages/unified/UnifiedHomePage';

const FeatureRoute = ({ children, fallback }) => {
  const { flags, loading } = useFeatureFlags();
  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }} role="status">
        <CircularProgress aria-label="Loading application features" />
      </Box>
    );
  }
  return flags.unified_reads ? children : <Navigate to={fallback} replace />;
};

const UnifiedWriteRoute = ({ children, fallback }) => {
  const { flags, loading } = useFeatureFlags();
  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }} role="status">
        <CircularProgress aria-label="Loading application features" />
      </Box>
    );
  }
  return flags.unified_reads && flags.unified_writes ? children : <Navigate to={fallback} replace />;
};

const HomeRoute = () => {
  const { flags, loading } = useFeatureFlags();
  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }} role="status">
        <CircularProgress aria-label="Loading application features" />
      </Box>
    );
  }
  return <Navigate to={flags.unified_reads ? '/home' : '/upload'} replace />;
};

const ProtectedRoutes = () => (
  <SidebarLayout>
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/home" element={<FeatureRoute fallback="/upload"><UnifiedHomePage /></FeatureRoute>} />
      <Route path="/launch" element={<UnifiedWriteRoute fallback="/products"><CanonicalLaunchWizard /></UnifiedWriteRoute>} />
      <Route path="/reconcile" element={<UnifiedWriteRoute fallback="/listings"><CanonicalReconcilePage /></UnifiedWriteRoute>} />
      <Route path="/catalog" element={<FeatureRoute fallback="/fulfillment/printify/catalog"><UnifiedCatalogPage /></FeatureRoute>} />
      <Route path="/designs" element={<FeatureRoute fallback="/gallery"><UnifiedDesignsPage /></FeatureRoute>} />
      <Route path="/designs/new" element={<UnifiedWriteRoute fallback="/designs"><CanonicalDesignEditor /></UnifiedWriteRoute>} />
      <Route path="/designs/:id" element={<UnifiedWriteRoute fallback="/designs"><CanonicalDesignEditor /></UnifiedWriteRoute>} />
      <Route path="/assets" element={<FeatureRoute fallback="/gallery"><UnifiedAssetsPage /></FeatureRoute>} />
      <Route path="/products" element={<FeatureRoute fallback="/fulfillment/printify/products"><UnifiedProductsPage /></FeatureRoute>} />
      <Route path="/products/new" element={<UnifiedWriteRoute fallback="/products"><CanonicalProductEditor /></UnifiedWriteRoute>} />
      <Route path="/products/:id" element={<UnifiedWriteRoute fallback="/products"><CanonicalProductEditor /></UnifiedWriteRoute>} />
      <Route path="/listings" element={<FeatureRoute fallback="/sales/etsy/listings"><UnifiedListingsPage /></FeatureRoute>} />
      <Route path="/listings/new" element={<UnifiedWriteRoute fallback="/listings"><CanonicalListingEditor /></UnifiedWriteRoute>} />
      <Route path="/listings/:id" element={<UnifiedWriteRoute fallback="/listings"><CanonicalListingEditor /></UnifiedWriteRoute>} />
      <Route path="/collections" element={<FeatureRoute fallback="/sales/etsy/sections"><UnifiedCollectionsPage /></FeatureRoute>} />
      <Route path="/connections" element={<FeatureRoute fallback="/fulfillment/printify/stores"><UnifiedConnectionsPage /></FeatureRoute>} />
      <Route path="/activity" element={<FeatureRoute fallback="/system/operations"><UnifiedActivityPage /></FeatureRoute>} />
      <Route path="/gallery" element={<ImagesPage />} />
      <Route path="/mockups/dynamic/accounts" element={<DynamicMockupsAccountsPage />} />
      <Route path="/mockups/dynamic/catalogs" element={<DynamicMockupsCatalogsPage />} />
      <Route path="/mockups/dynamic/mockups" element={<DynamicMockupsPage />} />
      <Route path="/fulfillment/printify/stores" element={<PrintifyStoresPage />} />
      <Route path="/fulfillment/printify/catalog" element={<PrintifyCatalogPage />} />
      <Route path="/fulfillment/printify/products" element={<ProductsPage />} />
      <Route path="/fulfillment/printify/orders" element={<PrintifyOrdersPage />} />
      <Route path="/system/operations" element={<OperationsPage />} />
      <Route path="/sales/etsy/shops" element={<ShopsPage />} />
      <Route path="/sales/etsy/sections" element={<SectionsPage />} />
      <Route path="/sales/etsy/listings" element={<ListingsPage />} />
      <Route path="/sales/woocommerce/sites" element={<WooCommerceSitesPage />} />
      <Route path="/sales/woocommerce/categories" element={<WooCommerceCategoriesPage />} />
      <Route path="/sales/woocommerce/listings" element={<WooCommerceListingsPage />} />
      <Route path="/sales/woocommerce/mockups" element={<WooCommerceMockupsPage />} />
      <Route path="/dynamic/accounts" element={<Navigate to="/mockups/dynamic/accounts" replace />} />
      <Route path="/dynamic/catalogs" element={<Navigate to="/mockups/dynamic/catalogs" replace />} />
      <Route path="/dynamic/mockups" element={<Navigate to="/mockups/dynamic/mockups" replace />} />
      <Route path="/shops" element={<Navigate to="/sales/etsy/shops" replace />} />
      <Route path="/sections" element={<Navigate to="/sales/etsy/sections" replace />} />
      <Route path="/upload" element={<UploadPage />} />
      <Route path="*" element={<Navigate to="/upload" replace />} />
    </Routes>
  </SidebarLayout>
);

const ProtectedApp = () => (
  <FeatureFlagsProvider>
    <ProtectedRoutes />
  </FeatureFlagsProvider>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <RealtimeProvider>
            <UploadProvider>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/*"
                  element={
                    <RequireAuth>
                      <ProtectedApp />
                    </RequireAuth>
                  }
                />
              </Routes>
            </UploadProvider>
          </RealtimeProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

ReactDOM.render(<App />, document.getElementById('root'));
